import React, { useState, useCallback, useId } from 'react';
import { Sparkles, AlertCircle, CheckCircle } from 'lucide-react';
import { ConnectionParam } from '@/types/playbook';
import SimpleLexicalEditor from './SimpleLexicalEditor';

interface ConnectionParamEditorProps {
  param: ConnectionParam;
  onUpdate: (updates: Partial<ConnectionParam>) => void;
  readOnly?: boolean;
}

const ConnectionParamEditor: React.FC<ConnectionParamEditorProps> = ({
  param,
  onUpdate,
  readOnly = false,
}) => {
  const [isGeneratingDescription, setIsGeneratingDescription] = useState(false);
  const [isGeneratingRegex, setIsGeneratingRegex] = useState(false);
  const [regexValidationResult, setRegexValidationResult] = useState<'valid' | 'invalid' | null>(null);
  
  // Use a stable ID that doesn't change when param.name changes
  const stableId = useId();

  const handleUpdate = useCallback((updates: Partial<ConnectionParam>) => {
    onUpdate(updates);
  }, [onUpdate]);


  // Validate regex pattern
  const validateRegex = useCallback((pattern: string) => {
    if (!pattern.trim()) {
      setRegexValidationResult(null);
      return;
    }
    
    try {
      new RegExp(pattern);
      setRegexValidationResult('valid');
    } catch {
      setRegexValidationResult('invalid');
    }
  }, []);

  const handleRegexChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    handleUpdate({ regexValidation: newValue });
    validateRegex(newValue);
  }, [handleUpdate, validateRegex]);

  // AI generation functions (placeholder for future implementation)
  const handleGenerateDescription = useCallback(async () => {
    setIsGeneratingDescription(true);
    // TODO: Implement AI description generation
    // For now, just simulate loading
    setTimeout(() => {
      setIsGeneratingDescription(false);
      // Example generated description
      const generatedDescription = `Parameter for ${param.name.toLowerCase().replace(/_/g, ' ')}. This field is used to configure the integration settings.`;
      handleUpdate({ description: generatedDescription });
    }, 1500);
  }, [param.name, handleUpdate]);

  const handleGenerateRegex = useCallback(async () => {
    setIsGeneratingRegex(true);
    // TODO: Implement AI regex generation
    // For now, just simulate loading
    setTimeout(() => {
      setIsGeneratingRegex(false);
      // Example generated regex based on parameter name
      let generatedRegex = '';
      const paramName = param.name.toLowerCase();
      if (paramName.includes('email')) {
        generatedRegex = '^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$';
      } else if (paramName.includes('url') || paramName.includes('endpoint')) {
        generatedRegex = '^https?:\\/\\/.+';
      } else if (paramName.includes('key') || paramName.includes('token')) {
        generatedRegex = '^[a-zA-Z0-9_-]{8,}$';
      } else {
        generatedRegex = '^.{1,}$'; // Basic non-empty validation
      }
      handleUpdate({ regexValidation: generatedRegex });
      validateRegex(generatedRegex);
    }, 1500);
  }, [param.name, handleUpdate, validateRegex]);

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-4">
      {/* 2x2 Grid Layout */}
      <div className="grid grid-cols-2 gap-4">
        {/* Row 1, Column 1: Parameter Name */}
        <div>
          <label htmlFor={`param-name-${stableId}`} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Parameter Name
          </label>
          <input
            id={`param-name-${stableId}`}
            type="text"
            value={param.name}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => !readOnly && handleUpdate({ name: e.target.value })}
            placeholder="e.g., API_KEY"
            disabled={readOnly}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 disabled:bg-gray-100 dark:disabled:bg-gray-700 disabled:text-gray-500 focus:ring focus:outline-none dark:focus:ring-blue-500/20 dark:focus:bg-gray-700 transition duration-300 ease-in-out focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {/* Row 1, Column 2: Example Value with Optional checkbox */}
        <div>
          <label htmlFor={`example-value-${stableId}`} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Example Value
          </label>
          <div className="flex items-center space-x-2">
            <input
              id={`example-value-${stableId}`}
              type="text"
              value={param.exampleValue || ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => !readOnly && handleUpdate({ exampleValue: e.target.value })}
              placeholder="e.g., your_api_key_here"
              disabled={readOnly}
              className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 disabled:bg-gray-100 dark:disabled:bg-gray-700 disabled:text-gray-500 focus:ring focus:outline-none dark:focus:ring-blue-500/20 dark:focus:bg-gray-700 transition duration-300 ease-in-out focus:ring-blue-500 focus:border-blue-500"
            />
            <div className="flex items-center space-x-2">
              <input
                id={`optional-${stableId}`}
                type="checkbox"
                checked={param.isOptional || false}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => !readOnly && handleUpdate({ isOptional: e.target.checked })}
                disabled={readOnly}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50"
              />
              <label htmlFor={`optional-${stableId}`} className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Optional
              </label>
            </div>
          </div>
        </div>

        {/* Row 2, Column 1: Default Value */}
        <div>
          <label htmlFor={`default-value-${stableId}`} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Default Value
          </label>
          <input
            id={`default-value-${stableId}`}
            type="text"
            value={param.defaultValue || ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => !readOnly && handleUpdate({ defaultValue: e.target.value })}
            placeholder="e.g., default_key"
            disabled={readOnly}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 disabled:bg-gray-100 dark:disabled:bg-gray-700 disabled:text-gray-500 focus:ring focus:outline-none dark:focus:ring-blue-500/20 dark:focus:bg-gray-700 transition duration-300 ease-in-out focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {/* Row 2, Column 2: Regex Validation */}
        <div>
          <label htmlFor={`regex-validation-${stableId}`} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Regex Validation Pattern
          </label>
          <div className="relative">
            <input
              id={`regex-validation-${stableId}`}
              type="text"
              value={param.regexValidation || ''}
              onChange={handleRegexChange}
              placeholder="e.g., ^[a-zA-Z0-9_-]{8,}$"
              disabled={readOnly || isGeneratingRegex}
              className={`w-full px-3 py-2 pr-20 border rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 disabled:bg-gray-100 dark:disabled:bg-gray-700 disabled:text-gray-500 focus:ring focus:outline-none dark:focus:ring-blue-500/20 dark:focus:bg-gray-700 transition duration-300 ease-in-out focus:ring-blue-500 focus:border-blue-500 ${
                regexValidationResult === 'valid' 
                  ? 'border-green-500 dark:border-green-400' 
                  : regexValidationResult === 'invalid' 
                  ? 'border-red-500 dark:border-red-400' 
                  : 'border-gray-300 dark:border-gray-600'
              }`}
            />
            
            {/* Validation indicator */}
            {regexValidationResult && (
              <div className="absolute right-12 top-1/2 -translate-y-1/2">
                {regexValidationResult === 'valid' ? (
                  <CheckCircle className="h-4 w-4 text-green-500" />
                ) : (
                  <AlertCircle className="h-4 w-4 text-red-500" />
                )}
              </div>
            )}
            
            {/* AI Generate button */}
            <button
              type="button"
              onClick={handleGenerateRegex}
              className={`absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md text-gray-500 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/30 ${!readOnly && !isGeneratingRegex ? '' : 'opacity-50 cursor-not-allowed'}`}
              title="Generate Regex with AI"
              disabled={readOnly || isGeneratingRegex}
            >
              {isGeneratingRegex ? (
                <div className="rounded-full h-4 w-4 border-b-2 border-blue-500 animate-spin"></div>
              ) : (
                <Sparkles className="h-4 w-4" />
              )}
            </button>
          </div>
          
          {regexValidationResult === 'invalid' && (
            <p className="mt-1 text-xs text-red-500 flex items-center">
              <AlertCircle className="h-3 w-3 mr-1" />
              Invalid regex pattern
            </p>
          )}
        </div>
      </div>

      {/* Description */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Description
          </label>
          <button
            type="button"
            onClick={handleGenerateDescription}
            className={`flex items-center gap-1 px-2 py-1 text-xs rounded-md text-gray-500 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/30 ${!readOnly && !isGeneratingDescription ? '' : 'opacity-50 cursor-not-allowed'}`}
            title="Generate Description with AI"
            disabled={readOnly || isGeneratingDescription}
          >
            {isGeneratingDescription ? (
              <div className="rounded-full h-3 w-3 border-b-2 border-blue-500 animate-spin"></div>
            ) : (
              <Sparkles className="h-3 w-3" />
            )}
            Generate
          </button>
        </div>
        
        <SimpleLexicalEditor
          value={param.description || ''}
          onChange={(value) => handleUpdate({ description: value })}
          placeholder="Describe this parameter and provide guidance on how to fill it..."
          disabled={readOnly || isGeneratingDescription}
          className="w-full"
        />
      </div>
    </div>
  );
};

export default ConnectionParamEditor;
